// ============================================================================
// Record-Routing data layer (Task 1 primitive).
// "Hand a record to a person/role with a note, track status, see history."
// Flat-fetch + client-join (no PostgREST embeds), useState/useEffect, RLS-aware.
// Writes funnel through SECURITY DEFINER RPCs: route_record / acknowledge_routing
// / resolve_routing.
// ============================================================================
import { ROUTE_TO_PERSON_ROLES, routingTokenFor } from '../../../shared/role-gates';
import { useEffect, useState } from 'react';
import { supabase } from '../../../shared/supabase';
import { useAuth } from '../../../shared/auth';
import { errorMessage } from '../../../shared/errors';

export type RoutingEntityType =
  | 'project'
  | 'sub_replacement_request'
  | 'facility_task'
  | 'new_account_sop';

export type RoutingStatus = 'Open' | 'Acknowledged' | 'Resolved' | 'Cancelled';
export type RoutingRole = NonNullable<ReturnType<typeof routingTokenFor>>;

export interface RoutingRow {
  routing_id: string;
  entity_type: RoutingEntityType;
  entity_id: string;
  routed_from_eid: string;
  routed_to_eid: string | null;
  routed_to_role: RoutingRole | null;
  reason: string | null;
  status: RoutingStatus;
  resolved_at: string | null;
  resolved_by_eid: string | null;
  created_at: string;
  updated_at: string;
  routed_from_name?: string | null;
  routed_to_name?: string | null;
  resolved_by_name?: string | null;
}

export interface RoutingEvent {
  event_id: number;
  routing_id: string;
  event_type: 'routed' | 'note' | 'status_change' | 'resolved';
  actor_eid: string | null;
  note: string | null;
  created_at: string;
  actor_name?: string | null;
}

const ROUTING_SELECT =
  'routing_id, entity_type, entity_id, routed_from_eid, routed_to_eid, routed_to_role, reason, status, resolved_at, resolved_by_eid, created_at, updated_at';

// role → routed_to_role token used for "addressed to my role" inbox matching.
function roleToken(role: string | null | undefined): RoutingRole | null {
  return routingTokenFor(role);
}

export function entityPath(t: RoutingEntityType, id: string): string {
  switch (t) {
    case 'project':
      return `/project-work/${id}`;
    case 'sub_replacement_request':
      return `/sub-replacement/${id}`;
    case 'facility_task':
      return `/tasks/${id}`;
    case 'new_account_sop':
      return '/new-account-sop';
    default:
      return '/';
  }
}

export const ROUTING_ENTITY_LABEL: Record<RoutingEntityType, string> = {
  project: 'Project',
  sub_replacement_request: 'Sub replacement',
  facility_task: 'Task',
  new_account_sop: 'New Account SOP',
};

async function fetchEmployeeNames(eids: string[]): Promise<Record<string, string>> {
  const ids = Array.from(new Set(eids.filter(Boolean)));
  if (ids.length === 0) return {};
  const { data, error } = await supabase
    .from('v_employees_am')
    .select('employee_id, employee_name_first, employee_name_last')
    .in('employee_id', ids);
  if (error) throw error;
  const map: Record<string, string> = {};
  for (const e of (data ?? []) as Array<{
    employee_id: string;
    employee_name_first: string | null;
    employee_name_last: string | null;
  }>) {
    map[e.employee_id] =
      `${e.employee_name_first ?? ''} ${e.employee_name_last ?? ''}`.trim() || e.employee_id;
  }
  return map;
}

// --- RPC wrappers (all writes go through SECURITY DEFINER functions) --------
export async function routeRecord(input: {
  entityType: RoutingEntityType;
  entityId: string;
  routedToEid?: string | null;
  routedToRole?: RoutingRole | null;
  reason: string;
}): Promise<string> {
  const { data, error } = await supabase.rpc('route_record', {
    p_entity_type: input.entityType,
    p_entity_id: input.entityId,
    p_routed_to_eid: input.routedToEid ?? null,
    p_routed_to_role: input.routedToRole ?? null,
    p_reason: input.reason.trim() || null,
  });
  if (error) throw error;
  return data as string;
}

export async function acknowledgeRouting(routingId: string, note?: string): Promise<void> {
  const { error } = await supabase.rpc('acknowledge_routing', {
    p_routing_id: routingId,
    p_note: note?.trim() || null,
  });
  if (error) throw error;
}

export async function resolveRouting(routingId: string, note?: string): Promise<void> {
  const { error } = await supabase.rpc('resolve_routing', {
    p_routing_id: routingId,
    p_note: note?.trim() || null,
  });
  if (error) throw error;
}

// --- My Inbox: items routed TO me (by eid) or to a role I hold -------------
export interface InboxResult {
  data: RoutingRow[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useInbox(): InboxResult {
  const { employee, profile } = useAuth();
  const myEid = employee?.employee_id ?? null;
  const myToken = roleToken(profile?.role);
  const [data, setData] = useState<RoutingRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError(null);
      try {
        const ors: string[] = [];
        if (myEid) ors.push(`routed_to_eid.eq.${myEid}`);
        if (myToken) ors.push(`routed_to_role.eq.${myToken}`);
        if (ors.length === 0) {
          if (!cancelled) {
            setData([]);
            setIsLoading(false);
          }
          return;
        }
        const { data: rows, error: e } = await supabase
          .from('record_routing')
          .select(ROUTING_SELECT)
          .in('status', ['Open', 'Acknowledged'])
          .or(ors.join(','))
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (e) throw e;
        const list = (rows ?? []) as RoutingRow[];
        const names = await fetchEmployeeNames([
          ...list.map((r) => r.routed_from_eid),
          ...list.map((r) => r.routed_to_eid ?? ''),
        ]);
        if (cancelled) return;
        setData(
          list.map((r) => ({
            ...r,
            routed_from_name: names[r.routed_from_eid] ?? r.routed_from_eid,
            routed_to_name: r.routed_to_eid ? names[r.routed_to_eid] ?? r.routed_to_eid : null,
          })),
        );
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'Failed to load your inbox.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myEid, myToken, tick]);

  return { data, isLoading, error, refetch: () => setTick((n) => n + 1) };
}

// Lightweight count for the nav badge. Self-handles the no-auth case.
export function useInboxCount(): number {
  const { employee, profile } = useAuth();
  const myEid = employee?.employee_id ?? null;
  const myToken = roleToken(profile?.role);
  const [count, setCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ors: string[] = [];
        if (myEid) ors.push(`routed_to_eid.eq.${myEid}`);
        if (myToken) ors.push(`routed_to_role.eq.${myToken}`);
        if (ors.length === 0) {
          if (!cancelled) setCount(0);
          return;
        }
        const { count: c, error } = await supabase
          .from('record_routing')
          .select('routing_id', { count: 'exact', head: true })
          .in('status', ['Open', 'Acknowledged'])
          .or(ors.join(','));
        if (cancelled) return;
        if (error) throw error;
        setCount(c ?? 0);
      } catch {
        if (!cancelled) setCount(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [myEid, myToken]);
  return count;
}

// --- Routing + events for one record (the mini activity log) ----------------
export interface RecordRoutingResult {
  routings: RoutingRow[];
  eventsByRouting: Record<string, RoutingEvent[]>;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useRecordRouting(
  entityType: RoutingEntityType | null,
  entityId: string | null,
): RecordRoutingResult {
  const [routings, setRoutings] = useState<RoutingRow[]>([]);
  const [eventsByRouting, setEventsByRouting] = useState<Record<string, RoutingEvent[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!entityType || !entityId) {
      setRoutings([]);
      setEventsByRouting({});
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: rows, error: e } = await supabase
          .from('record_routing')
          .select(ROUTING_SELECT)
          .eq('entity_type', entityType)
          .eq('entity_id', entityId)
          .order('created_at', { ascending: false });
        if (cancelled) return;
        if (e) throw e;
        const list = (rows ?? []) as RoutingRow[];
        const ids = list.map((r) => r.routing_id);
        let events: RoutingEvent[] = [];
        if (ids.length > 0) {
          const { data: evs, error: ee } = await supabase
            .from('record_routing_events')
            .select('event_id, routing_id, event_type, actor_eid, note, created_at')
            .in('routing_id', ids)
            .order('created_at', { ascending: true });
          if (cancelled) return;
          if (ee) throw ee;
          events = (evs ?? []) as RoutingEvent[];
        }
        const names = await fetchEmployeeNames([
          ...list.map((r) => r.routed_from_eid),
          ...list.map((r) => r.routed_to_eid ?? ''),
          ...list.map((r) => r.resolved_by_eid ?? ''),
          ...events.map((ev) => ev.actor_eid ?? ''),
        ]);
        if (cancelled) return;
        const byR: Record<string, RoutingEvent[]> = {};
        for (const ev of events) {
          (byR[ev.routing_id] ??= []).push({
            ...ev,
            actor_name: ev.actor_eid ? names[ev.actor_eid] ?? ev.actor_eid : null,
          });
        }
        setRoutings(
          list.map((r) => ({
            ...r,
            routed_from_name: names[r.routed_from_eid] ?? r.routed_from_eid,
            routed_to_name: r.routed_to_eid ? names[r.routed_to_eid] ?? r.routed_to_eid : null,
            resolved_by_name: r.resolved_by_eid ? names[r.resolved_by_eid] ?? r.resolved_by_eid : null,
          })),
        );
        setEventsByRouting(byR);
      } catch (err) {
        if (!cancelled)
          setError(errorMessage(err, 'Failed to load routing activity.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, tick]);

  return { routings, eventsByRouting, isLoading, error, refetch: () => setTick((n) => n + 1) };
}

// --- People you can route to (AM / finance / HR / leadership) ---------------
export interface RoutablePerson {
  employee_id: string;
  name: string;
  app_role: string | null;
}

export function useRoutablePeople(): {
  people: RoutablePerson[];
  isLoading: boolean;
  error: string | null;
} {
  const [people, setPeople] = useState<RoutablePerson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: e } = await supabase
          // Binds to v_employee_picker: Active AND pay_type='Salaried' AND
          // app_role IS NOT NULL (TCSD-0152). PICKERS EXCLUDE, RESOLVERS
          // RESOLVE (TCSD-0129) — this populates the routing assignee list, so
          // it asks "who could I choose". fetchEmployeeNames() above is the
          // resolver half of this module and stays on v_employees_am.
          // ⛔ DO NOT WIDEN THIS BACK TO v_employees_am. That view is the FULL
          // roster; binding a dropdown to it is the mistake this corrects.
          // employment_status is gone from BOTH the select and the filter: the
          // view has no such column and every row in it is Active by
          // definition. ⚠️ Naming a missing column fails at RUNTIME in
          // PostgREST, not at build — tsc will not catch it.
          .from('v_employee_picker')
          .select('employee_id, employee_name_first, employee_name_last, app_role')
          .in('app_role', ROUTE_TO_PERSON_ROLES)
          .order('employee_name_first');
        if (cancelled) return;
        if (e) throw e;
        setPeople(
          ((data ?? []) as Array<{
            employee_id: string;
            employee_name_first: string | null;
            employee_name_last: string | null;
            app_role: string | null;
          }>).map((r) => ({
            employee_id: r.employee_id,
            name: `${r.employee_name_first ?? ''} ${r.employee_name_last ?? ''}`.trim() || r.employee_id,
            app_role: r.app_role,
          })),
        );
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, 'Failed to load people.'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return { people, isLoading, error };
}
